import { Component, signal, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { ReproduccionService } from '../services/reproduccion.service';
import { ComunidadService } from '../services/comunidad.service';
import { BlockingService, BlockedUser } from '../services/blocking.service';
import { WalletService, WalletInfo, TipoCrypto } from '../services/wallet.service';
import { NotificationService } from '../services/notification.service';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

interface UserInfo {
  name: string;
  email: string;
  phone: string;
  memberSince: string;
  totalCourses: number;
  completedCourses: number;
  avatar: string;
  rol: string;
}

interface PlanInfo {
  nombre: string;
  precio: string;
  duracionDias: number;
  descripcion: string;
  esDefault: boolean;
}

interface SuscripcionInfo {
  fechaInicio: string;
  fechaFin: string;
  estado: string;
}

interface DispositivoRegistrado {
  id_dispositivo: number;
  nombre_dispositivo: string;
  tipo_dispositivo: string;
  ip_dispositivo: string;
  activo: boolean;
  fecha_registro: string;
  ultimo_acceso: string;
  es_dispositivo_actual: boolean;
}

interface AlertaSeguridad {
  id_alerta: number;
  tipo_alerta: string;
  severidad: 'CRITICAL' | 'WARNING' | 'INFO';
  mensaje: string;
  fecha_alerta: string;
  revisada: boolean;
  datos_adicionales?: any;
}

interface HistorialIP {
  ip_anterior: string | null;
  ip_nueva: string;
  fecha_cambio: string;
  alerta_enviada: boolean;
}

interface Payment {
  id: number;
  courseName: string;
  amount: number;
  currency: string;
  date: string;
  status: 'completed' | 'pending' | 'failed';
  paymentMethod: string;
}

interface CourseProgress {
  id: number;
  title: string;
  thumbnail: string;
  progress: number;
  totalLessons: number;
  completedLessons: number;
  lastAccessed: string;
  instructor: string;
}

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './profile.html',
  styleUrl: './profile.css',
})
export class ProfileComponent implements OnInit {
  private authService = inject(AuthService);
  private router = inject(Router);
  private reproduccionService = inject(ReproduccionService);
  private comunidadService = inject(ComunidadService);
  private blockingService = inject(BlockingService);
  private walletService = inject(WalletService);
  private notificationService = inject(NotificationService);

  activeTab = signal<'info' | 'payments' | 'progress' | 'community' | 'referrals' | 'blocked'>('info');
  showEditModal = signal<boolean>(false);
  showPasswordModal = signal<boolean>(false);

  // Referral Data
  referralData = signal<any>(null);
  isLoadingReferrals = signal(false);
  codigoReferido = signal<string>('');
  linkReferido = signal<string>('');

  // Blocked Users Data
  blockedUsers = signal<BlockedUser[]>([]);
  isLoadingBlockedUsers = signal(false);

  // Wallet Data
  walletInfo = signal<WalletInfo | null>(null);
  isLoadingWallet = signal(false);
  tiposCrypto = signal<TipoCrypto[]>([]);
  showWalletModal = signal(false);
  walletForm = signal({
    address: '',
    tipo: 'USDT',
    red: 'TRC20'
  });

  // Modal de edición de comunidad
  showCommunityEditModal = signal<boolean>(false);

  // Datos del formulario de edición
  editForm = {
    nombre: '',
    apellido: '',
    email: ''
  };

  // Datos del formulario de contraseña
  passwordForm = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  };

  // User List Modal (Followers/Following)
  showUserListModal = signal(false);
  userListType = signal<'followers' | 'following'>('followers');
  userListItems = signal<any[]>([]);
  isLoadingUserList = signal(false);
  userListPage = signal(1);
  hasMoreUserList = signal(false);

  // Estados del formulario
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';

  // Estados del formulario de contraseña
  isSubmittingPassword = false;
  passwordErrorMessage = '';
  passwordSuccessMessage = '';
  showCurrentPassword = false;
  showNewPassword = false;
  showConfirmPassword = false;

  // Datos de comunidad
  communityProfile = signal<any>(null);
  isLoadingCommunity = signal(false);

  // Formulario de edición de comunidad
  communityEditForm = {
    nombre_usuario: '',
    biografia: '',
    ciudad: '',
    sitio_web: '',
    es_perfil_publico: true
  };

  // Estados del formulario de comunidad
  isSubmittingCommunity = false;
  communityErrorMessage = '';
  communitySuccessMessage = '';

  // Archivos para subir
  profilePictureFile: File | null = null;
  coverPhotoFile: File | null = null;
  profilePicturePreview: string | null = null;
  coverPhotoPreview: string | null = null;

  userInfo: UserInfo = {
    name: '',
    email: '',
    phone: 'No especificado',
    memberSince: '',
    totalCourses: 0,
    completedCourses: 0,
    avatar: '',
    rol: 'Usuario'
  };

  planInfo: PlanInfo = {
    nombre: '',
    precio: '0',
    duracionDias: 0,
    descripcion: '',
    esDefault: false
  };

  suscripcionInfo: SuscripcionInfo = {
    fechaInicio: '',
    fechaFin: '',
    estado: ''
  };

  dispositivos: DispositivoRegistrado[] = [];
  totalDispositivos = 0;
  dispositivosActivos = 0;
  loadingDispositivos = false;

  alertas: AlertaSeguridad[] = [];
  estadisticasAlertas = { CRITICAL: 0, WARNING: 0, INFO: 0 };
  loadingAlertas = false;

  historialSeleccionado: HistorialIP[] = [];
  dispositivoHistorial: any = null;
  showHistorialModal = signal<boolean>(false);

  payments: Payment[] = [
    {
      id: 1,
      courseName: 'Advanced Web Development with React',
      amount: 129.99,
      currency: 'USD',
      date: '2024-10-15',
      status: 'completed',
      paymentMethod: 'Visa •••• 4242'
    },
    {
      id: 2,
      courseName: 'Complete Python Bootcamp',
      amount: 99.99,
      currency: 'USD',
      date: '2024-09-28',
      status: 'completed',
      paymentMethod: 'Mastercard •••• 8888'
    },
    {
      id: 3,
      courseName: 'UI/UX Design Fundamentals',
      amount: 89.99,
      currency: 'USD',
      date: '2024-09-10',
      status: 'completed',
      paymentMethod: 'Visa •••• 4242'
    },
    {
      id: 4,
      courseName: 'Data Science with Python',
      amount: 149.99,
      currency: 'USD',
      date: '2024-08-22',
      status: 'completed',
      paymentMethod: 'PayPal'
    },
    {
      id: 5,
      courseName: 'Machine Learning A-Z',
      amount: 159.99,
      currency: 'USD',
      date: '2024-08-05',
      status: 'completed',
      paymentMethod: 'Visa •••• 4242'
    }
  ];

  coursesProgress: CourseProgress[] = [
    {
      id: 1,
      title: 'Advanced Web Development with React',
      thumbnail: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&h=225&fit=crop',
      progress: 75,
      totalLessons: 48,
      completedLessons: 36,
      lastAccessed: '2 hours ago',
      instructor: 'Sarah Johnson'
    },
    {
      id: 2,
      title: 'Complete Python Bootcamp',
      thumbnail: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?w=400&h=225&fit=crop',
      progress: 60,
      totalLessons: 52,
      completedLessons: 31,
      lastAccessed: '1 day ago',
      instructor: 'Michael Chen'
    },
    {
      id: 3,
      title: 'UI/UX Design Fundamentals',
      thumbnail: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=400&h=225&fit=crop',
      progress: 45,
      totalLessons: 32,
      completedLessons: 14,
      lastAccessed: '3 days ago',
      instructor: 'Emily Rodriguez'
    },
    {
      id: 4,
      title: 'Data Science with Python',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
      progress: 30,
      totalLessons: 64,
      completedLessons: 19,
      lastAccessed: '5 days ago',
      instructor: 'David Park'
    },
    {
      id: 5,
      title: 'Machine Learning A-Z',
      thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=225&fit=crop',
      progress: 15,
      totalLessons: 72,
      completedLessons: 11,
      lastAccessed: '1 week ago',
      instructor: 'Lisa Thompson'
    }
  ];

  ngOnInit() {
    window.scrollTo(0, 0);
    this.loadUserData();
    this.loadDispositivos();
    this.loadAlertas();
    this.loadCommunityProfile();
    this.loadReferralData();
    this.loadBlockedUsers();
    this.loadWalletData();
    this.loadTiposCrypto();
  }

  /**
   * Carga los datos de referidos
   */
  loadReferralData() {
    this.isLoadingReferrals.set(true);

    console.log('🔄 Iniciando carga de datos de referidos...');

    // Cargar código y link de referido
    this.authService.obtenerMiCodigoReferido().subscribe({
      next: (codigoResponse) => {
        console.log('📋 Código de referido recibido:', codigoResponse);
        console.log('📋 OK?', codigoResponse.ok);
        console.log('📋 Código:', codigoResponse.codigo_referido);
        console.log('📋 Link:', codigoResponse.link);

        if (codigoResponse.ok) {
          const codigo = codigoResponse.codigo_referido || '';
          const link = codigoResponse.link || '';

          console.log('✅ Asignando código:', codigo);
          console.log('✅ Asignando link:', link);

          this.codigoReferido.set(codigo);
          this.linkReferido.set(link);
        } else {
          console.log('⚠️ Respuesta no OK, limpiando valores');
          this.codigoReferido.set('');
          this.linkReferido.set('');
        }
      },
      error: (error) => {
        console.error('❌ Error en subscribe al obtener código de referido:', error);
        this.codigoReferido.set('Error al cargar');
        this.linkReferido.set('Error al cargar');
      }
    });

    // Cargar lista de referidos y comisiones
    this.authService.obtenerMisReferidos().subscribe({
      next: (response) => {
        console.log('📊 Datos de referidos recibidos:', response);
        if (response.ok) {
          this.referralData.set(response);
        } else {
          this.referralData.set({
            ok: false,
            total_referidos: 0,
            resumen_estados: { pendientes: 0, aprobadas: 0, pagadas: 0, rechazadas: 0 },
            resumen_comisiones: {
              pendiente: 0,
              aprobada: 0,
              pagada: 0,
              rechazada: 0,
              total_generado: 0,
              total_cobrado: 0,
              pendiente_cobro: 0
            },
            referidos: []
          });
        }
        this.isLoadingReferrals.set(false);
      },
      error: (error) => {
        console.error('Error al cargar datos de referidos:', error);
        this.referralData.set({
          ok: false,
          total_referidos: 0,
          resumen_estados: { pendientes: 0, aprobadas: 0, pagadas: 0, rechazadas: 0 },
          resumen_comisiones: {
            pendiente: 0,
            aprobada: 0,
            pagada: 0,
            rechazada: 0,
            total_generado: 0,
            total_cobrado: 0,
            pendiente_cobro: 0
          },
          referidos: []
        });
        this.isLoadingReferrals.set(false);
      }
    });
  }

  /**
   * Copia el código de referido al portapapeles
   */
  copiarCodigoReferido() {
    const codigo = this.codigoReferido();
    if (codigo) {
      navigator.clipboard.writeText(codigo).then(() => {
        alert('¡Código copiado al portapapeles!');
      }).catch(err => {
        console.error('Error al copiar código:', err);
        alert('No se pudo copiar el código');
      });
    }
  }

  /**
   * Copia el link de referido al portapapeles
   */
  copiarLinkReferido() {
    const link = this.linkReferido();
    console.log('📋 Copiando link:', link);
    if (link && link !== '' && link !== 'Cargando...') {
      navigator.clipboard.writeText(link).then(() => {
        alert('¡Link copiado al portapapeles!');
      }).catch(err => {
        console.error('Error al copiar link:', err);
        alert('No se pudo copiar el link');
      });
    } else {
      alert('El link aún no está disponible. Por favor, espera un momento.');
    }
  }

  /**
   * Comparte el código de referido por WhatsApp
   */
  compartirWhatsApp() {
    const codigo = this.codigoReferido();
    const link = this.linkReferido();

    console.log('📱 Compartir WhatsApp - Código:', codigo, 'Link:', link);

    if (!codigo) {
      alert('No se ha cargado el código de referido. Por favor, recarga la página.');
      return;
    }

    let mensaje = `¡Únete conmigo a TradingLab! 🚀\n\nUsa mi código de referido: ${codigo}`;

    if (link && link !== '' && link !== 'undefined') {
      mensaje += `\n\nO regístrate directamente aquí:\n${link}`;
    }

    const url = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
    window.open(url, '_blank');
  }

  /**
   * Obtiene el color del badge según el estado de la comisión
   */
  getComisionBadgeClass(estado: string): string {
    switch (estado) {
      case 'pagada': return 'badge-success';
      case 'aprobada': return 'badge-info';
      case 'pendiente': return 'badge-warning';
      case 'rechazada': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  /**
   * Formatea el monto de dinero
   */
  formatearMonto(monto: number): string {
    return `$${monto.toFixed(2)}`;
  }

  // ==================== WALLET CRYPTO ====================

  /**
   * Carga los tipos de crypto soportados
   */
  loadTiposCrypto() {
    this.walletService.obtenerTiposCrypto().subscribe({
      next: (response) => {
        if (response.ok && response.tipos_soportados.length > 0) {
          this.tiposCrypto.set(response.tipos_soportados);
        } else {
          // Datos por defecto si el backend no los tiene
          this.tiposCrypto.set([
            {
              tipo: 'USDT',
              nombre: 'Tether',
              redes: ['TRC20', 'ERC20', 'BSC'],
              ejemplo_address: 'TXYZoPYKcsVnMwZXhkVqWgHFGmBv7Pqzxy'
            },
            {
              tipo: 'BTC',
              nombre: 'Bitcoin',
              redes: ['Bitcoin'],
              ejemplo_address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa'
            },
            {
              tipo: 'ETH',
              nombre: 'Ethereum',
              redes: ['Ethereum'],
              ejemplo_address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb2'
            },
            {
              tipo: 'USDC',
              nombre: 'USD Coin',
              redes: ['ERC20', 'BSC', 'Polygon'],
              ejemplo_address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb2'
            },
            {
              tipo: 'TRX',
              nombre: 'Tron',
              redes: ['TRC20'],
              ejemplo_address: 'TXYZoPYKcsVnMwZXhkVqWgHFGmBv7Pqzxy'
            }
          ]);
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos de crypto:', error);
        // Usar datos por defecto en caso de error
        this.tiposCrypto.set([
          {
            tipo: 'USDT',
            nombre: 'Tether',
            redes: ['TRC20', 'ERC20', 'BSC'],
            ejemplo_address: 'TXYZoPYKcsVnMwZXhkVqWgHFGmBv7Pqzxy'
          },
          {
            tipo: 'BTC',
            nombre: 'Bitcoin',
            redes: ['Bitcoin'],
            ejemplo_address: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa'
          },
          {
            tipo: 'ETH',
            nombre: 'Ethereum',
            redes: ['Ethereum'],
            ejemplo_address: '0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb2'
          }
        ]);
      }
    });
  }

  /**
   * Carga la información de la wallet del usuario
   */
  loadWalletData() {
    this.isLoadingWallet.set(true);
    this.walletService.obtenerMiWallet().subscribe({
      next: (response) => {
        console.log('💳 Wallet data recibida:', response);
        if (response.ok && response.wallet) {
          this.walletInfo.set(response.wallet);
        } else {
          this.walletInfo.set(null);
        }
        this.isLoadingWallet.set(false);
      },
      error: (error) => {
        console.error('Error al cargar wallet:', error);
        this.walletInfo.set(null);
        this.isLoadingWallet.set(false);
      }
    });
  }

  /**
   * Abre el modal para configurar wallet
   */
  abrirModalWallet() {
    const wallet = this.walletInfo();
    if (wallet && wallet.wallet_configurada) {
      // Si ya tiene wallet, pre-llenar el formulario
      this.walletForm.set({
        address: wallet.wallet_crypto_address,
        tipo: wallet.wallet_tipo,
        red: wallet.wallet_red
      });
    } else {
      // Reset el formulario
      this.walletForm.set({
        address: '',
        tipo: 'USDT',
        red: 'TRC20'
      });
    }
    this.showWalletModal.set(true);
  }

  /**
   * Cierra el modal de wallet
   */
  cerrarModalWallet() {
    this.showWalletModal.set(false);
  }

  /**
   * Guarda o actualiza la wallet
   */
  async guardarWallet() {
    const form = this.walletForm();

    // Validar que todos los campos estén llenos
    if (!form.address || !form.tipo || !form.red) {
      alert('Por favor, completa todos los campos');
      return;
    }

    // Validar formato de la dirección (pasando también la red)
    if (!this.walletService.validarFormatoWallet(form.address, form.tipo, form.red)) {
      alert(`La dirección de wallet no tiene un formato válido para ${form.tipo} en la red ${form.red}`);
      return;
    }

    const request = {
      wallet_crypto_address: form.address,
      wallet_tipo: form.tipo,
      wallet_red: form.red,
      metodo_pago_preferido: 'crypto' as const
    };

    try {
      const wallet = this.walletInfo();
      let response;

      if (wallet && wallet.wallet_configurada) {
        // Actualizar wallet existente
        response = await firstValueFrom(this.walletService.actualizarWallet(request));
      } else {
        // Configurar nueva wallet
        response = await firstValueFrom(this.walletService.configurarWallet(request));
      }

      if (response.ok) {
        alert(response.msg || 'Wallet configurada exitosamente');
        this.cerrarModalWallet();
        this.loadWalletData(); // Recargar datos
      } else {
        alert(response.msg || 'Error al configurar wallet');
      }
    } catch (error) {
      console.error('Error al guardar wallet:', error);
      alert('Error al guardar la wallet');
    }
  }

  /**
   * Elimina la wallet configurada
   */
  async eliminarWallet() {
    if (!confirm('¿Estás seguro de que deseas eliminar tu wallet? Ya no podrás recibir pagos de comisiones.')) {
      return;
    }

    try {
      const response = await firstValueFrom(this.walletService.eliminarWallet());
      if (response.ok) {
        alert(response.msg || 'Wallet eliminada exitosamente');
        this.loadWalletData(); // Recargar datos
      } else {
        alert(response.msg || 'Error al eliminar wallet');
      }
    } catch (error) {
      console.error('Error al eliminar wallet:', error);
      alert('Error al eliminar la wallet');
    }
  }

  /**
   * Verifica la wallet
   */
  async verificarWallet() {
    try {
      const response = await firstValueFrom(this.walletService.verificarWallet());
      if (response.ok) {
        alert(response.msg || 'Wallet verificada exitosamente');
        this.loadWalletData(); // Recargar datos
      } else {
        alert(response.msg || 'Error al verificar wallet');
      }
    } catch (error) {
      console.error('Error al verificar wallet:', error);
      alert('Error al verificar la wallet');
    }
  }

  /**
   * Obtiene las redes disponibles para un tipo de crypto
   */
  getRedesDisponibles(tipo: string): string[] {
    const tipoCrypto = this.tiposCrypto().find(t => t.tipo === tipo);
    return tipoCrypto?.redes || [];
  }

  /**
   * Actualiza el formulario cuando cambia el tipo de crypto
   */
  onTipoCryptoChange(tipo: string) {
    const redes = this.getRedesDisponibles(tipo);
    if (redes.length > 0) {
      this.walletForm.set({
        ...this.walletForm(),
        tipo: tipo,
        red: redes[0] // Seleccionar la primera red por defecto
      });
    }
  }

  /**
   * Actualiza la red de la wallet
   */
  onRedChange(red: string) {
    this.walletForm.set({
      ...this.walletForm(),
      red: red
    });
  }

  /**
   * Actualiza la dirección de la wallet
   */
  onAddressChange(address: string) {
    this.walletForm.set({
      ...this.walletForm(),
      address: address
    });
  }

  /**
   * Copia la dirección de wallet al portapapeles
   */
  copiarDireccionWallet() {
    const wallet = this.walletInfo();
    if (wallet && wallet.wallet_crypto_address) {
      navigator.clipboard.writeText(wallet.wallet_crypto_address).then(() => {
        alert('¡Dirección copiada al portapapeles!');
      }).catch(err => {
        console.error('Error al copiar dirección:', err);
        alert('No se pudo copiar la dirección');
      });
    }
  }

  /**
   * Carga la lista de usuarios bloqueados
   */
  loadBlockedUsers() {
    this.isLoadingBlockedUsers.set(true);
    this.blockingService.getBlockedUsers().subscribe({
      next: (response) => {
        console.log('📋 Usuarios bloqueados recibidos:', response);
        if (response.ok) {
          this.blockedUsers.set(response.bloqueados || []);
        } else {
          this.blockedUsers.set([]);
        }
        this.isLoadingBlockedUsers.set(false);
      },
      error: (error) => {
        console.error('Error loading blocked users:', error);
        this.blockedUsers.set([]);
        this.isLoadingBlockedUsers.set(false);
      }
    });
  }

  /**
   * Desbloquea un usuario
   */
  async unblockUser(idUsuario: number) {
    if (!confirm('¿Estás seguro de que deseas desbloquear a este usuario?')) {
      return;
    }

    try {
      const response = await firstValueFrom(this.blockingService.unblockUser(idUsuario));
      if (response.ok) {
        // Recargar la lista de bloqueados
        this.loadBlockedUsers();
      } else {
        alert(response.msg || 'Error al desbloquear usuario');
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      alert('Error al desbloquear usuario');
    }
  }

  /**
   * Carga los datos del usuario desde los signals del AuthService
   */
  loadUserData() {
    // Verificar que el usuario esté logueado
    if (!this.authService.isLoggedIn()) {
      this.router.navigate(['/login']);
      return;
    }

    // Obtener datos desde los signals del AuthService
    const usuario = this.authService.currentUser();
    const plan = this.authService.userPlan();
    const suscripcion = this.authService.userSubscription();

    if (usuario) {
      const fullName = `${usuario.nombre} ${usuario.apellido || ''}`.trim();

      this.userInfo = {
        name: fullName || 'Usuario',
        email: usuario.email || '',
        phone: 'No especificado',
        memberSince: this.formatMemberSince(suscripcion?.fecha_inicio),
        totalCourses: 0, // Esto se puede obtener de un endpoint
        completedCourses: 0, // Esto se puede obtener de un endpoint
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${fullName}`,
        rol: usuario.nombre_rol || 'Usuario'
      };

      console.log('✅ Datos del usuario cargados:', this.userInfo);
    }

    if (plan) {
      this.planInfo = {
        nombre: plan.nombre_plan || 'Sin plan',
        precio: plan.precio || '0',
        duracionDias: plan.duracion_dias || 0,
        descripcion: plan.descripcion || '',
        esDefault: plan.es_default || false
      };

      console.log('✅ Datos del plan cargados:', this.planInfo);
    }

    if (suscripcion) {
      this.suscripcionInfo = {
        fechaInicio: suscripcion.fecha_inicio || '',
        fechaFin: suscripcion.fecha_fin || '',
        estado: suscripcion.estado || 'Desconocido'
      };

      console.log('✅ Datos de la suscripción cargados:', this.suscripcionInfo);
    }
  }

  /**
   * Formatea la fecha de membresía
   */
  formatMemberSince(fechaInicio?: string): string {
    if (fechaInicio) {
      try {
        const fecha = new Date(fechaInicio);
        return fecha.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
      } catch {
        return 'Recientemente';
      }
    }
    return 'Recientemente';
  }

  /**
   * Formatea la fecha para mostrar
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
  }

  /**
   * Obtiene el color según el estado de la suscripción
   */
  getEstadoColor(estado: string): string {
    switch (estado.toLowerCase()) {
      case 'activa':
        return '#77d27a';
      case 'cancelada':
        return '#DC3D1A';
      case 'expirada':
        return '#ffa726';
      default:
        return '#ffffff';
    }
  }

  /**
   * Obtiene las iniciales del nombre del usuario
   */
  getUserInitials(): string {
    if (!this.userInfo.name) return 'U';

    const words = this.userInfo.name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }

    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  }

  setActiveTab(tab: 'info' | 'payments' | 'progress' | 'community' | 'referrals' | 'blocked') {
    this.activeTab.set(tab);
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'completed':
        return '#77d27a';
      case 'pending':
        return '#ffa726';
      case 'failed':
        return '#DC3D1A';
      default:
        return '#ffffff';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'pending':
        return 'Pendiente';
      case 'failed':
        return 'Fallido';
      default:
        return status;
    }
  }

  getTotalSpent(): number {
    return this.payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0);
  }

  goToCourse(courseId: number) {
    this.router.navigate(['/course', courseId]);
  }

  continueCourse(courseId: number) {
    this.router.navigate(['/course', courseId, 'watch']);
  }

  /**
   * Abre el modal de edición
   */
  openEditModal() {
    const usuario = this.authService.currentUser();
    if (usuario) {
      this.editForm = {
        nombre: usuario.nombre || '',
        apellido: usuario.apellido || '',
        email: usuario.email || ''
      };
    }
    this.errorMessage = '';
    this.successMessage = '';
    this.showEditModal.set(true);
  }

  /**
   * Cierra el modal de edición
   */
  closeEditModal() {
    this.showEditModal.set(false);
    this.errorMessage = '';
    this.successMessage = '';
  }

  /**
   * Carga los dispositivos vinculados usando el nuevo endpoint
   */
  loadDispositivos() {
    this.loadingDispositivos = true;
    this.reproduccionService.listarDispositivos().subscribe({
      next: (response) => {
        this.loadingDispositivos = false;
        if (response.ok && response.dispositivos) {
          this.dispositivos = response.dispositivos;
          this.totalDispositivos = response.total || 0;
          this.dispositivosActivos = response.activos || 0;

          console.log('✅ Dispositivos cargados:', this.dispositivos);
          console.log(`📊 Total: ${this.totalDispositivos} | Activos: ${this.dispositivosActivos}`);

          // Advertir sobre dispositivos inactivos
          const inactivos = response.dispositivos.filter(d => !d.activo);
          if (inactivos.length > 0) {
            console.warn(`⚠️ Dispositivos inactivos: ${inactivos.length}`, inactivos);
          }
        }
      },
      error: (error) => {
        this.loadingDispositivos = false;
        console.error('Error al cargar dispositivos:', error);
      }
    });
  }

  /**
   * Carga las alertas de seguridad
   */
  loadAlertas() {
    this.loadingAlertas = true;
    this.authService.getAlertas().subscribe({
      next: (response) => {
        this.loadingAlertas = false;
        if (response.ok) {
          this.alertas = response.alertas;
          this.estadisticasAlertas = response.estadisticas;
          console.log('✅ Alertas cargadas:', this.alertas);
        }
      },
      error: (error) => {
        this.loadingAlertas = false;
        console.error('Error al cargar alertas:', error);
      }
    });
  }

  /**
   * Abre el modal del historial de IPs
   */
  verHistorialIPs(dispositivo: DispositivoRegistrado) {
    this.authService.getHistorialIPs(dispositivo.id_dispositivo).subscribe({
      next: (response) => {
        if (response.ok) {
          this.historialSeleccionado = response.historial;
          this.dispositivoHistorial = response.dispositivo;
          this.showHistorialModal.set(true);
        }
      },
      error: (error) => {
        console.error('Error al cargar historial:', error);
      }
    });
  }

  /**
   * Cierra el modal del historial de IPs
   */
  closeHistorialModal() {
    this.showHistorialModal.set(false);
    this.historialSeleccionado = [];
    this.dispositivoHistorial = null;
  }

  /**
   * Obtiene el color de la severidad de la alerta
   */
  getSeveridadColor(severidad: string): string {
    switch (severidad) {
      case 'CRITICAL':
        return '#DC3D1A';
      case 'WARNING':
        return '#ffa726';
      case 'INFO':
        return '#0e40d8';
      default:
        return '#ffffff';
    }
  }

  /**
   * Obtiene el ícono de la severidad
   */
  getSeveridadIcono(severidad: string): string {
    switch (severidad) {
      case 'CRITICAL':
        return '🚨';
      case 'WARNING':
        return '⚠️';
      case 'INFO':
        return 'ℹ️';
      default:
        return '📢';
    }
  }

  /**
   * Formatea la fecha de manera amigable
   */
  formatFecha(dateString: string): string {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `Hace ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
    } else if (diffDays === 1) {
      return 'Ayer';
    } else if (diffDays < 7) {
      return `Hace ${diffDays} días`;
    } else {
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  /**
   * Abre el modal de cambio de contraseña
   */
  openPasswordModal() {
    this.passwordForm = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    this.passwordErrorMessage = '';
    this.passwordSuccessMessage = '';
    this.showCurrentPassword = false;
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.showPasswordModal.set(true);
  }

  /**
   * Cierra el modal de cambio de contraseña
   */
  closePasswordModal() {
    this.showPasswordModal.set(false);
    this.passwordErrorMessage = '';
    this.passwordSuccessMessage = '';
    this.passwordForm = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
  }

  /**
   * Alterna la visibilidad de la contraseña
   */
  togglePasswordVisibility(field: 'current' | 'new' | 'confirm') {
    switch (field) {
      case 'current':
        this.showCurrentPassword = !this.showCurrentPassword;
        break;
      case 'new':
        this.showNewPassword = !this.showNewPassword;
        break;
      case 'confirm':
        this.showConfirmPassword = !this.showConfirmPassword;
        break;
    }
  }

  /**
   * Cambia la contraseña del usuario
   */
  changePassword() {
    // Validaciones
    if (!this.passwordForm.currentPassword.trim()) {
      this.passwordErrorMessage = 'La contraseña actual es obligatoria';
      return;
    }

    if (!this.passwordForm.newPassword.trim()) {
      this.passwordErrorMessage = 'La nueva contraseña es obligatoria';
      return;
    }

    if (this.passwordForm.newPassword.length < 6) {
      this.passwordErrorMessage = 'La nueva contraseña debe tener al menos 6 caracteres';
      return;
    }

    if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
      this.passwordErrorMessage = 'Las contraseñas no coinciden';
      return;
    }

    if (this.passwordForm.currentPassword === this.passwordForm.newPassword) {
      this.passwordErrorMessage = 'La nueva contraseña debe ser diferente a la actual';
      return;
    }

    this.isSubmittingPassword = true;
    this.passwordErrorMessage = '';
    this.passwordSuccessMessage = '';

    // Cambiar la contraseña
    this.authService.resetPassword(this.passwordForm.newPassword).subscribe({
      next: (response) => {
        this.isSubmittingPassword = false;
        if (response.ok) {
          this.passwordSuccessMessage = '¡Contraseña cambiada exitosamente!';

          // Cerrar el modal después de 2 segundos
          setTimeout(() => {
            this.closePasswordModal();
          }, 2000);
        } else {
          this.passwordErrorMessage = response.msg || 'Error al cambiar la contraseña';
        }
      },
      error: (error) => {
        this.isSubmittingPassword = false;
        this.passwordErrorMessage = error.error?.msg || 'Error al cambiar la contraseña';
      }
    });
  }

  /**
   * Actualiza los datos del usuario
   */
  updateUserInfo() {
    // Validaciones
    if (!this.editForm.nombre.trim()) {
      this.errorMessage = 'El nombre es obligatorio';
      return;
    }

    if (!this.editForm.apellido.trim()) {
      this.errorMessage = 'El apellido es obligatorio';
      return;
    }

    if (!this.editForm.email.trim()) {
      this.errorMessage = 'El email es obligatorio';
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.editForm.email)) {
      this.errorMessage = 'El formato del email no es válido';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authService.updateUsuario(
      this.editForm.nombre,
      this.editForm.apellido,
      this.editForm.email
    ).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (response.ok) {
          this.successMessage = '¡Datos actualizados correctamente!';

          // Recargar los datos del perfil
          this.loadUserData();

          // Cerrar el modal después de 1.5 segundos
          setTimeout(() => {
            this.closeEditModal();
          }, 1500);
        } else {
          this.errorMessage = response.msg || 'Error al actualizar los datos';
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error.error?.msg || 'Error al actualizar los datos';
      }
    });
  }

  /**
   * Carga el perfil de comunidad del usuario
   */
  loadCommunityProfile() {
    this.isLoadingCommunity.set(true);
    this.comunidadService.obtenerMiPerfil().subscribe({
      next: (response) => {
        if (response.ok) {
          const user = response.usuario;
          this.communityProfile.set({
            ...user,
            id_usuario: user.id_usuario,
            // Normalizar estadísticas para acceso fácil y robusto
            total_seguidores: user.estadisticas?.total_seguidores ?? 0,
            total_seguidos: user.estadisticas?.total_seguidos ?? 0,
            total_publicaciones: user.estadisticas?.total_publicaciones ?? 0,
            // Asegurar que las listas existan
            lista_seguidores: user.lista_seguidores || [],
            lista_seguidos: user.lista_seguidos || []
          });

          // Pre-llenar el formulario de edición
          console.log('✅ Perfil de comunidad cargado:', response.usuario);
        } else {
          // Fallback mínimo si la respuesta no es OK
          this.ensureMinimumCommunityProfile();
        }
        this.isLoadingCommunity.set(false);
      },
      error: (error) => {
        console.error('Error al cargar perfil de comunidad:', error);
        this.ensureMinimumCommunityProfile();
        this.isLoadingCommunity.set(false);
      }
    });
  }

  private ensureMinimumCommunityProfile() {
    if (!this.communityProfile()) {
      const user = this.authService.getCurrentUser();
      this.communityProfile.set({
        id_usuario: user?.uid || 0,
        nombre_usuario: user?.nombre || 'usuario',
        total_seguidores: 0,
        total_seguidos: 0,
        total_publicaciones: 0,
        lista_seguidores: [],
        lista_seguidos: []
      });
    }
  }

  /**
   * Abre el modal de edición de comunidad
   */
  openCommunityEditModal() {
    const profile = this.communityProfile();
    if (profile) {
      this.communityEditForm = {
        nombre_usuario: profile.nombre_usuario || '',
        biografia: profile.biografia || '',
        ciudad: profile.ciudad || '',
        sitio_web: profile.sitio_web || '',
        es_perfil_publico: profile.es_perfil_publico !== false
      };

      this.profilePicturePreview = profile.foto_perfil || null;
      this.coverPhotoPreview = profile.foto_portada || null;
    }
    this.communityErrorMessage = '';
    this.communitySuccessMessage = '';
    this.showCommunityEditModal.set(true);
  }

  /**
   * Cierra el modal de edición de comunidad
   */
  closeCommunityEditModal() {
    this.showCommunityEditModal.set(false);
    this.communityErrorMessage = '';
    this.communitySuccessMessage = '';
    this.profilePictureFile = null;
    this.coverPhotoFile = null;
    this.profilePicturePreview = null;
    this.coverPhotoPreview = null;
  }

  /**
   * Maneja la selección de foto de perfil
   */
  handleProfilePictureSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.profilePictureFile = file;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.profilePicturePreview = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Maneja la selección de foto de portada
   */
  handleCoverPhotoSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.coverPhotoFile = file;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.coverPhotoPreview = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Guarda los cambios del perfil de comunidad
   */
  async saveCommunityProfile() {
    // Validaciones
    if (!this.communityEditForm.nombre_usuario.trim()) {
      this.communityErrorMessage = 'El nombre de usuario es obligatorio';
      return;
    }

    if (!this.communityEditForm.biografia.trim()) {
      this.communityErrorMessage = 'La biografía es obligatoria';
      return;
    }

    this.isSubmittingCommunity = true;
    this.communityErrorMessage = '';
    this.communitySuccessMessage = '';

    try {
      // Subir foto de perfil si se seleccionó
      if (this.profilePictureFile) {
        await firstValueFrom(this.comunidadService.subirFotoPerfil(this.profilePictureFile));
      }

      // Subir foto de portada si se seleccionó
      if (this.coverPhotoFile) {
        await firstValueFrom(this.comunidadService.subirFotoPortada(this.coverPhotoFile));
      }

      // Actualizar datos del perfil
      await firstValueFrom(this.comunidadService.actualizarPerfil(this.communityEditForm));

      this.communitySuccessMessage = '¡Perfil actualizado correctamente!';

      // Recargar los datos
      this.loadCommunityProfile();

      // Cerrar el modal después de 1.5 segundos
      setTimeout(() => {
        this.closeCommunityEditModal();
      }, 1500);
    } catch (error: any) {
      this.communityErrorMessage = error.error?.msg || 'Error al actualizar el perfil';
    } finally {
      this.isSubmittingCommunity = false;
    }
  }

  /**
   * Navega al feed de la comunidad
   */
  goToCommunityFeed() {
    this.router.navigate(['/community/feed']);
  }

  /**
   * Navega al perfil público del usuario
   */
  goToPublicProfile() {
    const profile = this.communityProfile();
    if (profile?.id_usuario) {
      this.router.navigate(['/community/profile', profile.id_usuario]);
    }
  }

  /**
   * Obtiene la URL de la foto de portada desde Google Cloud Storage
   */
  getCoverPhotoUrl(): string {
    const profile = this.communityProfile();
    if (!profile?.foto_portada) {
      return '';
    }

    // Si ya es una URL completa, la devolvemos directamente
    if (profile.foto_portada.startsWith('http')) {
      return profile.foto_portada;
    }

    // Construimos la URL de Google Cloud Storage (nueva estructura simplificada)
    return `https://storage.googleapis.com/traidinglab/perfiles/foto_portada/${profile.foto_portada}`;
  }

  /**
   * Obtiene la URL de la foto de perfil desde Google Cloud Storage
   */
  getProfilePhotoUrl(): string {
    const profile = this.communityProfile();
    if (!profile?.foto_perfil) {
      return this.getDefaultAvatar();
    }

    // Si ya es una URL completa, la devolvemos directamente
    if (profile.foto_perfil.startsWith('http')) {
      return profile.foto_perfil;
    }

    // Construimos la URL de Google Cloud Storage (nueva estructura simplificada)
    return `https://storage.googleapis.com/traidinglab/perfiles/foto_perfil/${profile.foto_perfil}`;
  }

  /**
   * Obtiene la URL de foto de perfil de un usuario específico
   */
  getProfilePhotoUrlForUser(foto_perfil: string | null | undefined): string | null {
    if (!foto_perfil || foto_perfil === 'default.png') return null;
    if (foto_perfil.startsWith('http')) return foto_perfil;
    return `https://storage.googleapis.com/traidinglab/perfiles/foto_perfil/${foto_perfil}`;
  }

  /**
   * Genera un avatar SVG con las iniciales del usuario
   */
  getDefaultAvatar(): string {
    const initials = this.getUserInitials();
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="168" height="168" viewBox="0 0 168 168">
        <rect fill="#0e40d8" width="168" height="168"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" 
              font-family="Arial, sans-serif" font-size="60" font-weight="600" fill="white">
          ${initials}
        </text>
      </svg>
    `;
    return 'data:image/svg+xml;base64,' + btoa(svg);
  }

  /**
   * Abre lista de seguidores
   */
  openFollowersList() {
    this.userListType.set('followers');
    this.showUserListModal.set(true);
    this.userListPage.set(1);
    this.userListItems.set([]);

    const profile = this.communityProfile();
    // Priorizamos cargar de la API para asegurar datos frescos
    this.loadUserList();
  }

  /**
   * Abre lista de seguidos
   */
  openFollowingList() {
    this.userListType.set('following');
    this.showUserListModal.set(true);
    this.userListPage.set(1);
    this.userListItems.set([]);

    const profile = this.communityProfile();
    // Priorizamos cargar de la API para asegurar datos frescos
    this.loadUserList();
  }

  /**
   * Cierra modal de lista de usuarios
   */
  closeUserListModal() {
    this.showUserListModal.set(false);
  }

  /**
   * Carga la lista de usuarios (seguidores o seguidos)
   */
  loadUserList(loadMore = false) {
    if (loadMore) {
      this.userListPage.update(p => p + 1);
    } else {
      this.userListPage.set(1);
      this.isLoadingUserList.set(true);
      // No limpiamos aquí para evitar parpadeo si ya hay items y es loadMore
      // pero si es carga inicial, ya limpiamos en openList
    }

    const page = this.userListPage();
    const type = this.userListType();

    console.log(`🔍 Intentando cargar ${type} (Dashboard) - Página ${page}`);

    let obs: Observable<any>;
    if (type === 'followers') {
      obs = this.comunidadService.obtenerMisSeguidores(20, page);
    } else {
      obs = this.comunidadService.obtenerMisSeguidos(20, page);
    }

    obs.subscribe({
      next: (response) => {
        console.log(`📥 Respuesta recibida para ${type}:`, response);
        if (response && response.ok !== false) {
          // Intentar obtener la lista de varias propiedades posibles
          const rawItems = response.seguidores || response.seguidos || response.usuarios || response.data || [];

          const newItems = (Array.isArray(rawItems) ? rawItems : []).map((item: any) => {
            const u = item.usuario || item.seguidor || item.seguido || item;
            return {
              ...u,
              id_usuario: Number(u.id_usuario || u.id || item.id_usuario || item.id_seguidor || item.id_seguido || 0),
              nombre_usuario: u.nombre_usuario || u.username || item.nombre_usuario || item.username || item.seguidor_nombre_usuario || item.seguido_nombre_usuario || 'usuario',
              nombre_completo: u.nombre_completo ||
                `${u.nombre || item.nombre || item.seguidor_nombre || item.seguido_nombre || ''} ${u.apellido || item.apellido || item.seguidor_apellido || item.seguido_apellido || ''}`.trim() ||
                u.nombre || u.nombre_usuario || 'Usuario',
              foto_perfil: this.getProfilePhotoUrlForUser(u.foto_perfil || item.foto_perfil || u.avatar || item.foto),
              siguiendo: item.siguiendo ?? u.siguiendo ?? (type === 'following' ? true : false)
            };
          });

          console.log(`✅ Procesados ${newItems.length} usuarios para la lista`, newItems);

          if (loadMore) {
            this.userListItems.update(items => [...items, ...newItems]);
          } else {
            this.userListItems.set(newItems);
          }
          this.hasMoreUserList.set(newItems.length === 20);
        } else {
          console.warn(`⚠️ loadUserList - Respuesta no válida para ${type}:`, response);
        }
        this.isLoadingUserList.set(false);
      },
      error: (error) => {
        console.error(`❌ Error al cargar ${type}:`, error);
        this.isLoadingUserList.set(false);
      }
    });
  }

  /**
   * Navega al perfil de un usuario
   */
  goToUser(userId: number) {
    this.closeUserListModal();
    this.router.navigate(['/community/profile', userId]);
  }

  /**
   * Seguir/Dejar de seguir desde la lista
   */
  toggleFollowInList(user: any) {
    const isFollowing = user.siguiendo;
    const userId = user.id_usuario;

    // Actualización optimista
    this.toggleFollowInListLocally(userId, !isFollowing);

    const obs = isFollowing
      ? this.comunidadService.dejarDeSeguir(userId)
      : this.comunidadService.seguirUsuario(userId);

    obs.subscribe({
      next: (response) => {
        if (response.ok) {
          // Actualizamos también el perfil principal si es necesario
          this.loadCommunityProfile();
        } else {
          // Revertir si hay error
          this.toggleFollowInListLocally(userId, isFollowing);
          if (response.msg) {
            this.notificationService.info('Información', response.msg);
          }
        }
      },
      error: () => {
        this.toggleFollowInListLocally(userId, isFollowing);
      }
    });
  }

  private toggleFollowInListLocally(userId: number, following: boolean) {
    this.userListItems.update(items =>
      items.map(item =>
        item.id_usuario === userId ? { ...item, siguiendo: following } : item
      )
    );
  }

  public userIdActual(): number {
    return this.authService.getCurrentUser()?.uid || 0;
  }
}
